import * as Cookies from '../../constants/cookies';
import { CookieService } from '../../services/cookie-service';

export class Auth0TokenRefreshHelper {
  // ===========================================================================================================
  // Multi-tab token refresh handling
  // Below is an async multi-tab "lock" system that will not allow multiple tabs to attempt token refresh at
  // near the same time using the same refresh token (one will fail and logout). This forces the "waiter" to let
  // the winner tab to finish and then use its newly minted tokens instead of attempting its own refresh
  // ===========================================================================================================

  SILENT_LOCK_KEY = 'ss.auth.silent.lock.v1';

  DEFAULTS = {
    ttlMs: 15000, // lock lifetime; winner renews every ttl/2
    acquireTimeoutMs: 20000, // max time to wait to acquire
    pollIntervalMs: 150, // fallback poll if no storage event
  };

  OWNER_ID = window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : `${String(Date.now())}-${String(Math.random())}`;

  renewTimer = null;
  hasLock = false;
  silentAuthErrorState = null;

  constructor(auth0Client, options = null) {
    this.auth0 = auth0Client;
    this.options = options || this.DEFAULTS;
    window.addEventListener('unload', () => {
      try {
        this.releaseTabSilentAuthLock();
      } catch {
        /* noop */
      }
    });
  }

  async getAccessToken(shouldSkipCache = false, onAcquireTokenSuccess = null, onAcquireTokenFailure = null, internalArgRetryOnce = true) {
    const cacheMode = shouldSkipCache ? 'off' : 'on';
    const gotLock = await this.getTabSilentAuthLock();

    if (!gotLock) {
      // Waiter path: pick up winner's token without negotiating a second one
      this.silentAuthErrorState = null;
      const deadline = this.now() + Math.min(this.options.acquireTimeoutMs, 4000); // keep it snappy
      while (this.now() < deadline) {
        try {
          // If the winner finished, cache-only will succeed and we return the SAME token.
          // eslint-disable-next-line no-await-in-loop
          const at = await this.auth0.getTokenSilently({ cacheMode: 'cache-only' });
          if (onAcquireTokenSuccess) {
            onAcquireTokenSuccess(at);
          }
          return at;
        } catch {
          // Not cached yet — wait briefly and retry.
          // eslint-disable-next-line no-await-in-loop
          await this.waitForChange(this.options.pollIntervalMs);
        }
      }

      // Almost last resort: ONE-TIME re-entry into the full lock flow instead of blind refresh.
      // This lets another tab legitimately acquire the next lock without risking refresh-token reuse.
      if (internalArgRetryOnce) {
        // tiny randomized backoff to avoid stampede
        const jitter = 40 + Math.floor(Math.random() * 120);
        await new Promise((r) => setTimeout(r, jitter));
        return this.getAccessToken(
          /* shouldSkipCache */ false,
          onAcquireTokenSuccess,
          onAcquireTokenFailure,
          /* internalArgRetryOnce */ false, // prevent infinite loop
        );
      }

      // ultra-last resort (blindly refreshing token, no locks)...
      try {
        const at = await this.auth0.getTokenSilently({ cacheMode: 'on' });
        if (onAcquireTokenSuccess) {
          onAcquireTokenSuccess(at);
        }
        return at;
      } catch {
        if (onAcquireTokenFailure) {
          onAcquireTokenFailure(new Error('SilentAuthWaiterTimeout'));
        }
        throw new Error('SilentAuthWaiterTimeout');
      }
    }

    if (this.silentAuthErrorState) {
      // something is wrong with local state... don't pummel the auth0 api every request
      throw new Error('Auth0SilentRefreshErrorStateExists');
    }

    return this.auth0
      .getTokenSilently({ cacheMode })
      .then((at) => {
        this.silentAuthErrorState = null;
        if (onAcquireTokenSuccess) {
          onAcquireTokenSuccess(at);
        }
        this.promoteAuth0CookieDomain();
        CookieService.setCookieValueWithPathAndDomain(Cookies.AccessTokenV3, at);
        CookieService.setCookieValueWithPathAndDomain(Cookies.AccessTokenV2, at);

        return at;
      })
      .catch((err) => {
        if (onAcquireTokenFailure) {
          onAcquireTokenFailure(err);
        }
        if (err?.error === 'login_required') {
          // other types of refresh errors should not set this state (it will stop the wrapper from attempting auth0 token refresh)
          this.silentAuthErrorState = err;
        }
        throw err;
      })
      .finally(() => {
        this.releaseTabSilentAuthLock();
      });
  }

  // we want to be able to accept "cross-origin" tld cookie so that other partitions will honor auth0 SSO state. we
  // remove the subdomain cookie (e.g. ship9.shipstation.com) and replace it with a tld (.shipstation.com). this
  // needs to recur on every exchange
  promoteAuth0CookieDomain() {
    // remove the cookie placed on auth subdomain and add one at TLD. the split at the end is for dev ports
    const hostWithoutPort = window.location.host.split(':')[0];
    CookieService.removeCookie(Cookies.Auth0IsAuthenticated, hostWithoutPort);
    CookieService.setCookieValueWithExpiration(Cookies.Auth0IsAuthenticated, 'true', 2);
    CookieService.removeCookie(Cookies.Auth0IsClientAuthenticated, hostWithoutPort);
    CookieService.setCookieValueWithExpiration(Cookies.Auth0IsClientAuthenticated, 'true', 2);
  }

  now() {
    return Date.now();
  }

  readLock() {
    const raw = localStorage.getItem(this.SILENT_LOCK_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  writeLock(ownerId, expiresAt) {
    localStorage.setItem(this.SILENT_LOCK_KEY, JSON.stringify({ ownerId, expiresAt }));
  }

  clearLock() {
    localStorage.removeItem(this.SILENT_LOCK_KEY);
  }

  startRenew(ttlMs) {
    this.stopRenew();
    const interval = Math.max(1000, Math.floor(ttlMs / 2));
    this.renewTimer = window.setInterval(() => {
      const lk = this.readLock();
      if (!lk || lk.ownerId !== this.OWNER_ID) {
        this.stopRenew();
        this.hasLock = false;
        return;
      }
      this.writeLock(this.OWNER_ID, this.now() + ttlMs);
    }, interval);
  }

  stopRenew() {
    if (this.renewTimer !== null) {
      clearInterval(this.renewTimer);
      this.renewTimer = null;
    }
  }

  waitForChange(ms) {
    return new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        window.removeEventListener('storage', onStorage);
        resolve();
      };
      const t = setTimeout(finish, ms);

      const onStorage = (e) => {
        if (e.key === this.SILENT_LOCK_KEY || (e.key && e.key.startsWith(`${this.SILENT_LOCK_KEY}.`))) {
          clearTimeout(t);
          finish();
        }
      };

      window.addEventListener('storage', onStorage);
    });
  }

  // Try to acquire; returns true if THIS tab owns the lock
  async getTabSilentAuthLock(opts = null) {
    const cfg = opts || this.options;
    const { ttlMs, acquireTimeoutMs, pollIntervalMs } = cfg;
    const deadline = this.now() + acquireTimeoutMs;

    // Fast path: already own
    if (this.hasLock) {
      this.writeLock(this.OWNER_ID, this.now() + ttlMs);
      this.startRenew(ttlMs);
      return true;
    }

    // If we ever see another owner during this attempt, we will NOT acquire in this round.
    // We'll wait for that owner's cycle to finish, then return false so the caller
    // can do a cache-only pickup instead of negotiating a second token.
    let sawForeignOwner = false;

    while (this.now() < deadline) {
      const lk = this.readLock();

      if (!lk || lk.expiresAt <= this.now()) {
        // Lock is free/expired right now.
        // If we previously saw a foreign owner this round, we refuse to acquire
        // and let the caller take the waiter path (cache-only pickup).
        if (sawForeignOwner) {
          return false; // become a waiter for this round
        }
        // Try to claim
        this.writeLock(this.OWNER_ID, this.now() + ttlMs);
        const confirm = this.readLock();
        if (confirm && confirm.ownerId === this.OWNER_ID) {
          this.hasLock = true;
          this.startRenew(ttlMs);
          // Nudge listeners
          localStorage.setItem(`${this.SILENT_LOCK_KEY}.ping`, String(this.now()));
          localStorage.removeItem(`${this.SILENT_LOCK_KEY}.ping`);
          return true;
        }
        // Lost the race; fall through to wait
      } else if (lk.ownerId && lk.ownerId !== this.OWNER_ID) {
        // Lock exists; if another tab owns it, mark waiter mode.
        sawForeignOwner = true;
      }

      const msLeft = lk ? Math.max(0, lk.expiresAt - this.now()) : pollIntervalMs;
      // eslint-disable-next-line no-await-in-loop
      await this.waitForChange(Math.min(pollIntervalMs, msLeft + 10));
    }

    return false; // timed out
  }

  // Release only if THIS tab owns it
  releaseTabSilentAuthLock() {
    const lk = this.readLock();
    if (lk && lk.ownerId === this.OWNER_ID) {
      this.clearLock();
      localStorage.setItem(`${this.SILENT_LOCK_KEY}.released`, String(this.now()));
      localStorage.removeItem(`${this.SILENT_LOCK_KEY}.released`);
    }
    this.hasLock = false;
    this.stopRenew();
  }

  decodeJwtPayload(jwt) {
    const [, payload] = jwt.split('.');
    if (!payload) throw new Error('Invalid JWT');

    // base64url -> base64
    const b64 = payload
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(payload.length / 4) * 4, '=');

    // Decode to UTF-8 JSON
    const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
    const json = new TextDecoder().decode(bytes);
    return JSON.parse(json);
  }

  getJwtExp(jwt) {
    const { exp } = this.decodeJwtPayload(jwt);
    if (typeof exp !== 'number') throw new Error('No exp claim');
    return exp; // seconds since epoch
  }

  isJwtExpired(jwt, skewSeconds = 60) {
    const exp = this.getJwtExp(jwt);
    const now = Math.floor(Date.now() / 1000);
    return now >= exp - skewSeconds;
  }
}
